/**
 * Шилжүүлгийн хүлээн авалтын баримт — `GET /transfers/:id/receipt`.
 *
 * Салбар агуулахаас ирсэн барааг хүлээн авч баталгаажуулсны дараа
 * татдаг (вэбийн «Хүлээн авалт», 2026-10-03). Жинхэнэ `.xlsx` (`xlsx.ts`):
 * CSV-ээр гаргахад баганын өргөнгүй тул шошго тасарч, огноо `########`
 * болж байв.
 *
 * Бүтэц: гарчиг, толгойн мэдээлэл (шошго A:B, утга C:G нэгтгэсэн), хоосон
 * мөр, доор нь гутал бүр нэг мөр. Дутуу ирсэн нь ЭХЭНДЭЭ — зөрүү
 * доогуур нь живэхгүй.
 *
 * ⚠️ Загварын нэр ОРНО — захиалагчийн шууд хүсэлт (2026-10-03). Дэлгэцийн
 *    «зөвхөн баркод» дүрмээс ухамсартай үл хамаарах зүйл.
 * ⚠️ Үнэ нь ОДООГИЙН `items.price_mnt` — хүлээн авсан мөчийнх биш. Тиймээс
 *    толгой нь «Үнэ (одоогийн)».
 * ⚠️ Толгойн утгууд (огноо, дүн) ТЕКСТ — уншихад зориулсан, `formatMnt`-ээр.
 *    Хүснэгтийн үнэ нь ТОО (`#,##0₮`) — Excel-д нийлбэр бодогдоно.
 */
import { Decimal } from 'decimal.js';
import { formatMnt, formatQty } from './format.js';
import { ulaanbaatarDateTimeText } from './sales-export.js';
import { xlsxFile } from './xlsx.js';
export const TRANSFER_STATUS_MN = {
    DRAFT: 'Бэлтгэж буй',
    IN_TRANSIT: 'Замд',
    RECEIVED: 'Хүлээн авсан',
    PARTIAL: 'Дутуу ирсэн',
};
/** №, Баркод, Размер, Загвар, Үнэ, Төлөв, Дутуу гутал хаана */
const WIDTHS = [6, 18, 9, 22, 15, 11, 30];
const HEADER = ['№', 'Баркод', 'Размер', 'Загвар', 'Үнэ (одоогийн)', 'Төлөв', 'Дутуу гутал одоо хаана'];
/** Толгойн мэдээлэл эхлэх мөр (1 — гарчиг, 2 — хоосон) */
const META_ROW = 3;
const text = (v) => (v ? { text: v } : null);
const sum = (lines) => lines.reduce((a, l) => a.plus(l.priceMnt), new Decimal(0)).toFixed(2);
const when = (at) => (at ? ulaanbaatarDateTimeText(at) : null);
function itemRow(l, i) {
    return [
        { num: i + 1 },
        text(l.serial),
        l.size === null ? null : { num: l.size },
        { text: l.modelName },
        { num: new Decimal(l.priceMnt).toFixed(2), style: 'money' },
        l.arrived ? { text: 'Ирсэн' } : { text: 'Дутуу', style: 'alert' },
        l.arrived ? null : text(l.locationName),
    ];
}
export function transferReceiptSheet(r) {
    const arrived = r.lines.filter((l) => l.arrived);
    const missing = r.lines.filter((l) => !l.arrived);
    const meta = [
        ['Шилжүүлэг', text(r.code)],
        ['Төлөв', { text: TRANSFER_STATUS_MN[r.status], ...(missing.length ? { style: 'alert' } : {}) }],
        ['Хаанаас', text(r.fromStoreName)],
        ['Салбар (хүлээн авсан)', text(r.toStoreName)],
        ['Илгээсэн огноо', text(when(r.sentAt))],
        ['Илгээсэн ажилтан', text(r.sentByName)],
        ['Хүлээн авсан огноо', text(when(r.receivedAt))],
        ['Хүлээн авсан ажилтан', text(r.receivedByName)],
        ['Илгээсэн тоо', text(formatQty(r.lines.length))],
        ['Хүлээн авсан тоо', text(formatQty(arrived.length))],
        ['Дутуу тоо', { text: formatQty(missing.length), ...(missing.length ? { style: 'alert' } : {}) }],
        ['Илгээсэн нийт үнэ', text(formatMnt(sum(r.lines)))],
        ['Хүлээн авсан нийт үнэ', text(formatMnt(sum(arrived)))],
        ['Тэмдэглэл', text(r.notes)],
    ];
    const last = META_ROW + meta.length - 1;
    return {
        name: 'Хүлээн авалт',
        widths: WIDTHS,
        rows: [
            [{ text: `Хүлээн авалтын баримт · ${r.code}`, style: 'title' }],
            [],
            ...meta.map(([label, value]) => [{ text: label, style: 'label' }, null, value]),
            [],
            HEADER.map((h) => ({ text: h, style: 'header' })),
            ...[...missing, ...arrived].map(itemRow),
        ],
        merges: [
            'A1:G1',
            ...Array.from({ length: last - META_ROW + 1 }, (_, i) => META_ROW + i)
                .flatMap((n) => [`A${n}:B${n}`, `C${n}:G${n}`]),
        ],
    };
}
/** Татах файлын байтууд. */
export function transferReceiptXlsx(r) {
    return xlsxFile(transferReceiptSheet(r));
}
//# sourceMappingURL=transfer-receipt.js.map